import type Phaser from "phaser";
import type { VfxPlayer } from "../../../vfx";
import {
  coneTelegraphPoints,
  playTimedAttackFx,
  resolveAttackFx,
} from "../../../vfx";
import { infiniteWarCatalog } from "../../../data/infinite-war-catalog";
import type { IwRoleId, IwTeam } from "../infinite-war-rules";
import {
  ART_PACK,
  ATTACK_FX,
  FIELD,
  factionColor,
  factionDamageColor,
  factionDamageColorCss,
} from "./layout";
import {
  isIwSpellGimmick,
  resolveIwSpellVfx,
  type IwSpellFxPhase,
  type IwSpellGimmick,
} from "./spell-vfx-map";
import type { UnitsViewHost } from "./units-view";
import { noteAttackMotion } from "./units-view";

const CONE_DEG = infiniteWarCatalog.cards.shapes.cone.halfAngleDeg * 2;

export function playDemigodBurst(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  team: IwTeam,
): void {
  const color = factionDamageColor(team);
  const ring = scene.add
    .circle(x, y, 16, color, 0.14)
    .setDepth(7)
    .setStrokeStyle(3, color, 0.95);
  scene.tweens.add({
    targets: ring,
    scale: radius / 16,
    alpha: 0,
    duration: 430,
    ease: "Quad.easeOut",
    onComplete: () => ring.destroy(),
  });
}

export function playUnitAttack(
  host: UnitsViewHost,
  vfx: VfxPlayer,
  unitId: number,
  roleId: IwRoleId,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  team: IwTeam,
  heal: boolean,
): void {
  const length = Math.hypot(targetX - x, targetY - y) || 1;
  const dx = (targetX - x) / length;
  const dy = (targetY - y) / length;
  noteAttackMotion(host, unitId, dx, dy);
  const fx = resolveAttackFx(ATTACK_FX, roleId);
  const affiliation = team === "south" ? "ally" : "enemy";
  const angle = Math.atan2(dy, dx);
  playTimedAttackFx({
    scene: host.scene,
    vfx,
    fx,
    affiliation,
    packId: fx.spriteFxPackId ?? ART_PACK,
    originX: x,
    originY: y,
    impactX: targetX,
    impactY: targetY,
    angle,
    swingSec: 0.28,
    cellPx: 64,
    depth: 8,
    floatDepth: 12,
    targetKind: heal ? "ally" : "enemy",
    floatVars: { color: factionDamageColorCss(team) },
  });
}

function playDirectionalWaveGeom(
  scene: Phaser.Scene,
  x: number,
  y: number,
  angle: number,
  team: IwTeam,
): void {
  const color = factionDamageColor(team);
  const veil = factionColor(team, "veil");
  const hw = 22;
  const len = 110;
  const fx = Math.cos(angle);
  const fy = Math.sin(angle);
  const px = -fy;
  const py = fx;
  const tipX = x + fx * len;
  const tipY = y + fy * len;
  const g = scene.add.graphics().setDepth(8);
  g.fillStyle(color, 0.22);
  g.beginPath();
  g.moveTo(x + px * hw, y + py * hw);
  g.lineTo(tipX + px * hw * 0.55, tipY + py * hw * 0.55);
  g.lineTo(tipX - px * hw * 0.55, tipY - py * hw * 0.55);
  g.lineTo(x - px * hw, y - py * hw);
  g.closePath();
  g.fillPath();
  g.lineStyle(2.5, veil, 0.9);
  g.strokePath();
  for (let i = 0; i < 6; i += 1) {
    const t = 0.2 + i * 0.12;
    const mote = scene.add
      .circle(x + fx * len * t, y + fy * len * t, 4 + (i % 2), color, 0.85)
      .setDepth(9);
    scene.tweens.add({
      targets: mote,
      x: x + fx * (len + 40 + i * 18),
      y: y + fy * (len + 40 + i * 18),
      alpha: 0,
      scale: 1.6,
      duration: 360,
      delay: i * 28,
      onComplete: () => mote.destroy(),
    });
  }
  scene.tweens.add({
    targets: g,
    alpha: 0,
    duration: 420,
    ease: "Quad.easeOut",
    onComplete: () => g.destroy(),
  });
}

function playConeGeom(
  scene: Phaser.Scene,
  x: number,
  y: number,
  angle: number,
  range: number,
  team: IwTeam,
): void {
  const color = factionDamageColor(team);
  const pts = coneTelegraphPoints(x, y, angle, range, CONE_DEG, 14);
  const g = scene.add.graphics().setDepth(8);
  g.fillStyle(color, 0.18);
  g.beginPath();
  g.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i += 1) g.lineTo(pts[i]!.x, pts[i]!.y);
  g.closePath();
  g.fillPath();
  g.lineStyle(2.8, color, 0.92);
  g.strokePath();
  scene.tweens.add({
    targets: g,
    alpha: 0,
    duration: 480,
    ease: "Cubic.easeOut",
    onComplete: () => g.destroy(),
  });
}

function playZoneGeom(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  team: IwTeam,
  residual: boolean,
): void {
  const color = factionDamageColor(team);
  const r = Math.max(24, radius);
  const disk = scene.add.circle(x, y, r, color, residual ? 0.12 : 0.2).setDepth(7);
  const ring = scene.add
    .circle(x, y, r, color, 0)
    .setStrokeStyle(3, color, 0.95)
    .setDepth(8)
    .setScale(0.35);
  scene.tweens.add({
    targets: disk,
    alpha: 0,
    scale: residual ? 1.08 : 1.15,
    duration: residual ? 640 : 420,
    onComplete: () => disk.destroy(),
  });
  scene.tweens.add({
    targets: ring,
    scale: 1,
    alpha: 0,
    duration: residual ? 560 : 380,
    ease: "Cubic.easeOut",
    onComplete: () => ring.destroy(),
  });
}

function playDelayedGeom(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  team: IwTeam,
  phase: IwSpellFxPhase,
): void {
  const color = factionDamageColor(team);
  const r = Math.max(28, radius);
  if (phase === "telegraph") {
    const warn = scene.add
      .circle(x, y, r, color, 0)
      .setStrokeStyle(2.5, color, 0.85)
      .setDepth(8)
      .setScale(0.7);
    scene.tweens.add({
      targets: warn,
      scale: 1,
      alpha: { from: 0.9, to: 0.25 },
      duration: 520,
      yoyo: true,
      repeat: 1,
      onComplete: () => warn.destroy(),
    });
    const core = scene.add.circle(x, y, 8, color, 0.55).setDepth(9);
    scene.tweens.add({
      targets: core,
      scale: 1.8,
      alpha: 0.2,
      duration: 900,
      onComplete: () => core.destroy(),
    });
    return;
  }
  const blast = scene.add.circle(x, y, r * 0.35, color, 0.85).setDepth(9);
  const outer = scene.add
    .circle(x, y, r, color, 0)
    .setStrokeStyle(4, color, 0.95)
    .setDepth(8)
    .setScale(0.2);
  scene.tweens.add({
    targets: blast,
    scale: 3.2,
    alpha: 0,
    duration: 420,
    ease: "Cubic.easeOut",
    onComplete: () => blast.destroy(),
  });
  scene.tweens.add({
    targets: outer,
    scale: 1.05,
    alpha: 0,
    duration: 520,
    ease: "Cubic.easeOut",
    onComplete: () => outer.destroy(),
  });
}

function playBlightGeom(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  team: IwTeam,
): void {
  const color = factionDamageColor(team);
  const r = Math.max(24, radius);
  const mire = scene.add.circle(x, y, r, color, 0.22).setDepth(7);
  scene.tweens.add({
    targets: mire,
    alpha: 0,
    scale: 1.2,
    duration: 720,
    ease: "Sine.easeOut",
    onComplete: () => mire.destroy(),
  });
  for (let i = 0; i < 8; i += 1) {
    const a = (i / 8) * Math.PI * 2;
    const mote = scene.add
      .circle(x + Math.cos(a) * r * 0.45, y + Math.sin(a) * r * 0.45, 3.5, color, 0.8)
      .setDepth(8);
    scene.tweens.add({
      targets: mote,
      y: mote.y - 22,
      alpha: 0,
      duration: 480,
      delay: i * 30,
      onComplete: () => mote.destroy(),
    });
  }
}

function playBoardHalfGeom(scene: Phaser.Scene, team: IwTeam): void {
  const color = factionDamageColor(team);
  const midY = FIELD.y + FIELD.h * 0.5;
  const bandY = team === "south" ? FIELD.y : midY;
  const bandH = FIELD.h * 0.5;
  const band = scene.add
    .rectangle(FIELD.x + FIELD.w * 0.5, bandY + bandH * 0.5, FIELD.w, bandH, color, 0.22)
    .setDepth(7);
  const edge = scene.add
    .rectangle(FIELD.x + FIELD.w * 0.5, midY, FIELD.w, 4, color, 0.9)
    .setDepth(8);
  scene.tweens.add({
    targets: [band, edge],
    alpha: 0,
    duration: 560,
    ease: "Quad.easeOut",
    onComplete: () => {
      band.destroy();
      edge.destroy();
    },
  });
}

function playSpellGeometry(
  scene: Phaser.Scene,
  spell: IwSpellGimmick,
  x: number,
  y: number,
  team: IwTeam,
  angle: number | undefined,
  radius: number | undefined,
  phase: IwSpellFxPhase,
): void {
  const r = radius ?? 64;
  if (spell === "shockwave" && angle != null) {
    playDirectionalWaveGeom(scene, x, y, angle, team);
    return;
  }
  if (spell === "cone" && angle != null) {
    playConeGeom(scene, x, y, angle, r, team);
    return;
  }
  if (spell === "zone") {
    playZoneGeom(scene, x, y, r, team, false);
    return;
  }
  if (spell === "delayed_blast") {
    playDelayedGeom(scene, x, y, r, team, phase);
    return;
  }
  if (spell === "curse_cluster") {
    playBlightGeom(scene, x, y, r, team);
    return;
  }
  if (spell === "sacrifice_nuke") {
    playZoneGeom(scene, x, y, r, team, false);
    const core = scene.add.circle(x, y, 14, factionDamageColor(team), 0.9).setDepth(9);
    scene.tweens.add({
      targets: core,
      scale: 2.8,
      alpha: 0,
      duration: 360,
      onComplete: () => core.destroy(),
    });
    return;
  }
  if (spell === "board_half") {
    playBoardHalfGeom(scene, team);
  }
}

/** Shockwave launch cue — same path as spell_fx(shockwave). */
export function playFactionShockwave(
  scene: Phaser.Scene,
  vfx: VfxPlayer,
  x: number,
  y: number,
  angle: number,
  team: IwTeam,
): void {
  playSpellFx(scene, vfx, "shockwave", x, y, team, { angle, phase: "impact" });
}

export function playSpellFx(
  scene: Phaser.Scene,
  vfx: VfxPlayer,
  spell: string,
  x: number,
  y: number,
  team: IwTeam,
  opts?: {
    angle?: number;
    radius?: number;
    phase?: IwSpellFxPhase;
  },
): void {
  if (!isIwSpellGimmick(spell)) return;
  const phase: IwSpellFxPhase = opts?.phase ?? "impact";
  const resolved = resolveIwSpellVfx(spell, team, phase);
  const tint = factionDamageColor(team);
  const angle = opts?.angle ?? (spell === "board_half" ? (team === "south" ? -Math.PI / 2 : Math.PI / 2) : 0);
  vfx.play(resolved.primary, {
    x,
    y,
    angle,
    tint,
    depth: 8,
    affiliation: team === "south" ? "ally" : "enemy",
  });
  playSpellGeometry(scene, spell, x, y, team, opts?.angle, opts?.radius, phase);
  if (resolved.residual && phase === "impact") {
    scene.time.delayedCall(160, () => {
      if (!scene.sys.isActive()) return;
      vfx.play(resolved.residual!, {
        x,
        y,
        angle,
        tint,
        depth: 7,
      });
      if (spell === "zone" || spell === "curse_cluster") {
        playZoneGeom(scene, x, y, opts?.radius ?? 64, team, true);
      }
    });
  }
}

export function playSoulFade(
  scene: Phaser.Scene,
  x: number,
  y: number,
  team: IwTeam,
): void {
  const color = factionColor(team, "veil");
  for (let i = 0; i < 6; i += 1) {
    const mote = scene.add.circle(x, y, 3 + (i % 3), color, 0.85).setDepth(8);
    const ang = (i / 6) * Math.PI * 2;
    scene.tweens.add({
      targets: mote,
      x: x + Math.cos(ang) * (14 + i * 3),
      y: y + Math.sin(ang) * (14 + i * 3) - 18,
      alpha: 0,
      duration: 400,
      delay: i * 16,
      ease: "Quad.easeOut",
      onComplete: () => mote.destroy(),
    });
  }
}

export function playConversionFlash(
  scene: Phaser.Scene,
  x: number,
  y: number,
  team: IwTeam,
): void {
  const flash = scene.add
    .circle(x, y, 12, factionDamageColor(team), 0.75)
    .setDepth(7);
  scene.tweens.add({
    targets: flash,
    scale: 2.4,
    alpha: 0,
    duration: 260,
    onComplete: () => flash.destroy(),
  });
}
